import BillingsTaxiTaxiDetailsResponse, {
  BillingsTaxiTaxiDetailsResponseData,
  BillingsTaxiTaxiDetailsResponseDataContribution,
  BillingsTaxiTaxiDetailsResponseDataContributionType,
  BillingsTaxiTaxiDetailsResponseDataNode,
  BillingsTaxiTaxiDetailsResponseDataOrderForwardingAction,
  BillingsTaxiTaxiDetailsResponseDataOrderForwardingActionContract,
  BillingsTaxiTaxiDetailsResponseDataOrderForwardingActionContractBillingModel,
  BillingsTaxiTaxiDetailsResponseDataOrderForwardingActionContractTaxiDetails,
  BillingsTaxiTaxiDetailsResponseDataOrderForwardingActionTransportingOrder,
  BillingsTaxiTaxiDetailsResponseDataOrderForwardingActionTransportingOrderCargoOrder,
  BillingsTaxiTaxiDetailsResponseDataStatus,
} from "../../../../api/raily/billings/taxi-taxi/details/billings-taxi-taxi-details.response";
import BillingModel from "../../../../types/billing-model";
import BillingStatus from "../../../../types/billing-status";
import BillingsTaxiTaxiDetails, {
  BillingsTaxiTaxiDetailsContribution,
  BillingsTaxiTaxiDetailsContributionType,
  BillingsTaxiTaxiDetailsNode,
  BillingsTaxiTaxiDetailsOrderForwardingAction,
  BillingsTaxiTaxiDetailsOrderForwardingActionContract,
  BillingsTaxiTaxiDetailsOrderForwardingActionContractTaxiDetails,
  BillingsTaxiTaxiDetailsOrderForwardingActionTransportingOrder,
  BillingsTaxiTaxiDetailsOrderForwardingActionTransportingOrderCargoOrder,
} from "./billings-taxi-taxi-details";

const createContributionType = (
  type:
    | BillingsTaxiTaxiDetailsResponseDataContributionType.BONUS_OTHER
    | BillingsTaxiTaxiDetailsResponseDataContributionType.PENALTY_WRONG_ROUTE
    | BillingsTaxiTaxiDetailsResponseDataContributionType.PENALTY_BEING_LATE
    | BillingsTaxiTaxiDetailsResponseDataContributionType.PENALTY_INCOMPATIBLE_CAR
    | BillingsTaxiTaxiDetailsResponseDataContributionType.PENALTY_OTHER,
):
  | BillingsTaxiTaxiDetailsContributionType.BONUS_OTHER
  | BillingsTaxiTaxiDetailsContributionType.PENALTY_WRONG_ROUTE
  | BillingsTaxiTaxiDetailsContributionType.PENALTY_BEING_LATE
  | BillingsTaxiTaxiDetailsContributionType.PENALTY_INCOMPATIBLE_CAR
  | BillingsTaxiTaxiDetailsContributionType.PENALTY_OTHER => {
  switch (type) {
    case BillingsTaxiTaxiDetailsResponseDataContributionType.BONUS_OTHER:
      return BillingsTaxiTaxiDetailsContributionType.BONUS_OTHER;
    case BillingsTaxiTaxiDetailsResponseDataContributionType.PENALTY_WRONG_ROUTE:
      return BillingsTaxiTaxiDetailsContributionType.PENALTY_WRONG_ROUTE;
    case BillingsTaxiTaxiDetailsResponseDataContributionType.PENALTY_BEING_LATE:
      return BillingsTaxiTaxiDetailsContributionType.PENALTY_BEING_LATE;
    case BillingsTaxiTaxiDetailsResponseDataContributionType.PENALTY_INCOMPATIBLE_CAR:
      return BillingsTaxiTaxiDetailsContributionType.PENALTY_INCOMPATIBLE_CAR;
    case BillingsTaxiTaxiDetailsResponseDataContributionType.PENALTY_OTHER:
      return BillingsTaxiTaxiDetailsContributionType.PENALTY_OTHER;
  }
};

const createContribution = (
  contribution: BillingsTaxiTaxiDetailsResponseDataContribution,
): BillingsTaxiTaxiDetailsContribution => {
  switch (contribution.type) {
    case BillingsTaxiTaxiDetailsResponseDataContributionType.BONUS_FAVORABLE_DISTANCE:
      return {
        type: BillingsTaxiTaxiDetailsContributionType.BONUS_FAVORABLE_DISTANCE,
        comment: contribution.comment,
        distance: contribution.distance,
        rate: contribution.rate,
      };
    case BillingsTaxiTaxiDetailsResponseDataContributionType.BONUS_OTHER:
    case BillingsTaxiTaxiDetailsResponseDataContributionType.PENALTY_BEING_LATE:
    case BillingsTaxiTaxiDetailsResponseDataContributionType.PENALTY_INCOMPATIBLE_CAR:
    case BillingsTaxiTaxiDetailsResponseDataContributionType.PENALTY_OTHER:
    case BillingsTaxiTaxiDetailsResponseDataContributionType.PENALTY_WRONG_ROUTE:
      return {
        type: createContributionType(contribution.type),
        comment: contribution.comment,
        amount: contribution.amount,
      };
  }
};

const createContributions = (
  contributions: BillingsTaxiTaxiDetailsResponseDataContribution[],
): BillingsTaxiTaxiDetailsContribution[] => {
  return contributions
    .map(createContribution)
    .filter((contribution) => contribution);
};

const createNode = (
  node: BillingsTaxiTaxiDetailsResponseDataNode,
): BillingsTaxiTaxiDetailsNode => {
  return {
    allowCharge: node.allowCharge,
    checkoutDate: node.checkoutDate,
    checkoutEventUuid: node.checkoutEventId,
    description: node.description,
    distance: node.distance,
    editableCoordinates: node.editableCoordinates,
    haltingAmount: node.haltingAmount,
    haltingTime: node.haltingTime,
    highwayCharge: node.highwayCharge,
    lat: node.lat,
    lon: node.lon,
    nodeUuid: node.nodeId,
    orderUuid: node.orderId,
    plannedDate: node.plannedDate,
    plannedDistance: node.plannedDistance,
    uuid: node.id,
  };
};

const createNodes = (
  nodes: BillingsTaxiTaxiDetailsResponseDataNode[],
): BillingsTaxiTaxiDetailsNode[] => {
  return nodes.map(createNode);
};

const createModel = (
  model: BillingsTaxiTaxiDetailsResponseDataOrderForwardingActionContractBillingModel,
): BillingModel => {
  switch (model) {
    case BillingsTaxiTaxiDetailsResponseDataOrderForwardingActionContractBillingModel.AB:
      return BillingModel.AB;
    case BillingsTaxiTaxiDetailsResponseDataOrderForwardingActionContractBillingModel.ABA:
      return BillingModel.ABA;
    case BillingsTaxiTaxiDetailsResponseDataOrderForwardingActionContractBillingModel.OTHER:
      return BillingModel.OTHER;
    case BillingsTaxiTaxiDetailsResponseDataOrderForwardingActionContractBillingModel.SABS:
      return BillingModel.SABS;
  }
};

const createTaxi = (
  taxi: BillingsTaxiTaxiDetailsResponseDataOrderForwardingActionContractTaxiDetails,
): BillingsTaxiTaxiDetailsOrderForwardingActionContractTaxiDetails => {
  return {
    displayName: taxi.displayName,
    uuid: taxi.id,
  };
};

const createContract = (
  contract: BillingsTaxiTaxiDetailsResponseDataOrderForwardingActionContract,
): BillingsTaxiTaxiDetailsOrderForwardingActionContract => {
  return {
    consumerTaxi: createTaxi(contract.consumerTaxi),
    distanceRate: contract.distanceRate,
    haltingTimeAfterMinutes: contract.haltingTimeAfterMinutes,
    haltingTimeRate: contract.haltingTimeRate,
    model: createModel(contract.model),
    producerTaxi: createTaxi(contract.producerTaxi),
  };
};

const createCargoOrder = (
  cargo: BillingsTaxiTaxiDetailsResponseDataOrderForwardingActionTransportingOrderCargoOrder,
): BillingsTaxiTaxiDetailsOrderForwardingActionTransportingOrderCargoOrder => {
  return {
    humanId: cargo.humanId,
    uuid: cargo.id,
  };
};

const createTransportingOrder = (
  transportingOrder: BillingsTaxiTaxiDetailsResponseDataOrderForwardingActionTransportingOrder,
): BillingsTaxiTaxiDetailsOrderForwardingActionTransportingOrder => {
  return {
    cargoOrder: createCargoOrder(transportingOrder.cargoOrder),
  };
};

const createForwardingAction = (
  action: BillingsTaxiTaxiDetailsResponseDataOrderForwardingAction,
): BillingsTaxiTaxiDetailsOrderForwardingAction => {
  return {
    contract: createContract(action.contract),
    transportingOrder: createTransportingOrder(action.transportingOrder),
    uuid: action.id,
  };
};

const createStatus = (
  status: BillingsTaxiTaxiDetailsResponseDataStatus,
): BillingStatus => {
  switch (status) {
    case BillingsTaxiTaxiDetailsResponseDataStatus.ACCEPTED:
      return BillingStatus.ACCEPTED;
    case BillingsTaxiTaxiDetailsResponseDataStatus.CREATED:
      return BillingStatus.CREATED;
    case BillingsTaxiTaxiDetailsResponseDataStatus.REJECTED:
      return BillingStatus.REJECTED;
    case BillingsTaxiTaxiDetailsResponseDataStatus.REOPENED:
      return BillingStatus.REOPENED;
    case BillingsTaxiTaxiDetailsResponseDataStatus.REOPEN_REQUEST:
      return BillingStatus.REOPEN_REQUEST;
  }
};

const createData = (
  data: BillingsTaxiTaxiDetailsResponseData,
): BillingsTaxiTaxiDetails => {
  return {
    allContributionsAmount: data.allContributionsAmount,
    amountForChargeHaltings: data.amountForChargeHaltings,
    amountForChargeHighways: data.amountForChargeHighways,
    amountForDistance: data.amountForDistance,
    billingContributions: createContributions(data.billingContributions),
    billingNodes: createNodes(data.billingNodes),
    date: data.date,
    distance: data.distance,
    orderForwardingAction: createForwardingAction(data.orderForwardingAction),
    passengers: data.passengers,
    planEntryUuid: data.planEntryId,
    status: createStatus(data.status),
    sumOfBonuses: data.sumOfBonuses,
    sumOfPenalties: data.sumOfPenalties,
  };
};

const create = (
  response: BillingsTaxiTaxiDetailsResponse,
): BillingsTaxiTaxiDetails => {
  return createData(response.data);
};

const billingsTaxiTaxiDetailsFactory = {
  create,
};

export default billingsTaxiTaxiDetailsFactory;
